import * as React from "react";
import ReactDOM from "react-dom";
import styled, { css } from "styled-components";
import { rgba } from "polished";

import { ProductPageInner } from "src/pages/Customer/Products/ViewProductDetailsPage";
import useTerminology from "src/core/shared/hooks/useTerminology";
import { Icons } from "src/library/components/Icon/types";
import { Button } from "src/library/components/Button";
import { useOutsideItemClick } from "src/library/hooks";

interface ProductQuickViewContextState {
  setQuickViewProduct: (productId: number) => void;
}

export const ProductQuickViewContext = React.createContext<ProductQuickViewContextState>({ setQuickViewProduct: () => null });

export const QuickViewProductProvider: React.ReactFCWithChildren = ({ children }) => {
  const terminology = useTerminology();
  const [ quickViewProductId, setQuickViewProductId ] = React.useState<number | null>(null);
  const QuickViewRef = React.useRef<HTMLDivElement>(null);
  const [ openClass, setOpenClass ] = React.useState("");

  useOutsideItemClick([ QuickViewRef ], () => {
    setQuickViewProductId(null);
  });

  React.useEffect(() => {
    if (quickViewProductId) {
      setTimeout(() => {
        setOpenClass("is-open");
      }, 100);
    } else {
      setOpenClass("");
    }
  }, [ quickViewProductId ]);

  return (
    <ProductQuickViewContext.Provider value={{ setQuickViewProduct: setQuickViewProductId }}>
      {children}

      {quickViewProductId && ReactDOM.createPortal(
        <StyledQuickViewProduct className={`${openClass} quick-view-product`}>
          <div
            className="quick-view-inner"
            ref={QuickViewRef}
          >
            <Button
              $buttonType="plain"
              className="go-back"
              icon={Icons.CHEVRONLEFT}
              iconPosition="left"
              onClick={() => setQuickViewProductId(null)}
            >
              Go back
            </Button>

            {terminology && (
              <div className="quick-view-product-inner">
                <ProductPageInner
                  showSideBar={false}
                  productId={quickViewProductId}
                  terminology={terminology}
                />
              </div>
            )}
          </div>
        </StyledQuickViewProduct>, document.getElementById("main-wrapper") as HTMLElement)}
    </ProductQuickViewContext.Provider>
  );
};

export const useQuickViewProduct = () => React.useContext<ProductQuickViewContextState>(ProductQuickViewContext);

const StyledQuickViewProduct = styled.div`
  ${({ theme }) => css`
    position: fixed;
    width: 100vw;
    height: 100vh;
    z-index: 10000;
    background: ${rgba(theme.colors.midGrey, 0.6)};
    top: 0;
    left: 0;

    &.is-open {
      .quick-view-inner {
        transform: scaleY(1);
      }
    }
    
    .quick-view-inner {
      transition: transform 0.4s ease-in-out;
      margin: auto;
      display: flex;
      flex-direction: column;
      width: 90vw;
      height: 90vh;
      transform: scaleY(0);
      margin-top: calc(${theme.spacing.margins.xl} * 2);
      margin-bottom: calc(${theme.spacing.margins.xl} * 2);
      left: 50%;
      background: ${theme.colors.lightestGrey};
      padding: ${theme.spacing.padding.lg};
      overflow-y: scroll;
      overflow-x: hidden;
      -webkit-overflow-scrolling: touch;
      z-index: 10000;
      border: 2px solid ${theme.colors.lightGrey};
      border-radius: ${theme.borderRadius};
      box-shadow: ${theme.shadows.default};

      .quick-view-product-inner {
        display: flex;
        flex-direction: row;
      }

      .go-back {
        margin-bottom: ${theme.spacing.margins.md};
      }
    }
  `}
`;
